import {
  createFleetOperatingUnitDraft,
  type FleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import type { OperatorOrganizationRecord } from "@/lib/api/client"

export interface FleetOnboardingOwnerProfileSeed {
  email?: string | null
  name?: string | null
  phoneNumber?: string | null
}

const DEFAULT_PRIMARY_UNIT = {
  code: "HQ-001",
  name: "Primary Branch",
  type: "branch",
} as const

function toCleanValue(value: unknown) {
  return typeof value === "string" ? value.trim() : ""
}

function readMetadataString(metadata: Record<string, unknown> | null, keys: string[]) {
  if (!metadata) {
    return ""
  }

  for (const key of keys) {
    const normalized = toCleanValue(metadata[key])
    if (normalized) {
      return normalized
    }
  }

  return ""
}

export function applyProvisionedFleetDraft(
  draft: FleetOnboardingDraft,
  organization: OperatorOrganizationRecord | null,
  ownerProfile: FleetOnboardingOwnerProfileSeed | null
): FleetOnboardingDraft {
  if (!organization) {
    return draft
  }

  const metadata =
    organization.fleetMetadata
    && typeof organization.fleetMetadata === "object"
    && !Array.isArray(organization.fleetMetadata)
      ? (organization.fleetMetadata as Record<string, unknown>)
      : null
  const organizationName = toCleanValue(organization.name)
  const legalName = toCleanValue(organization.legalName)
  const adminName =
    toCleanValue(organization.fleetAdminName) || toCleanValue(ownerProfile?.name)
  const adminEmail =
    toCleanValue(organization.fleetAdminEmail) || toCleanValue(ownerProfile?.email)
  const adminPhone =
    toCleanValue(organization.fleetAdminPhone) || toCleanValue(ownerProfile?.phoneNumber)
  const primaryCity = toCleanValue(organization.city)
  const primaryCountry = toCleanValue(organization.country)
  const primaryRegion = toCleanValue(organization.region)
  const operatingRegions =
    readMetadataString(metadata, [
      "operatingRegions",
      "operating_regions",
      "primaryOperatingRegions",
      "primary_operating_regions",
    ]) || primaryRegion
  const preferredPublicLanguage =
    readMetadataString(metadata, [
      "preferredPublicLanguage",
      "preferred_public_language",
      "publicLanguage",
      "public_language",
    ]) || draft.preferredPublicLanguage
  const preferredDriverLanguage =
    readMetadataString(metadata, [
      "preferredDriverLanguage",
      "preferred_driver_language",
      "preferredRiderLanguage",
      "preferred_rider_language",
    ]) || draft.preferredDriverLanguage

  const primaryUnit =
    draft.units[0]
    || createFleetOperatingUnitDraft({
      code: DEFAULT_PRIMARY_UNIT.code,
      isPrimary: true,
      name: DEFAULT_PRIMARY_UNIT.name,
      type: DEFAULT_PRIMARY_UNIT.type,
    })
  const primaryUnitCode =
    readMetadataString(metadata, ["primaryUnitCode", "primary_unit_code"])
    || primaryUnit.code
    || DEFAULT_PRIMARY_UNIT.code
  const primaryUnitName =
    readMetadataString(metadata, ["primaryUnitName", "primary_unit_name"])
    || primaryUnit.name
    || DEFAULT_PRIMARY_UNIT.name
  const primaryUnitType =
    readMetadataString(metadata, ["primaryUnitType", "primary_unit_type"])
    || primaryUnit.type
    || DEFAULT_PRIMARY_UNIT.type

  const units = [
    {
      ...primaryUnit,
      city: primaryCity || primaryUnit.city,
      code: primaryUnitCode,
      escalationPhone: adminPhone || primaryUnit.escalationPhone,
      isPrimary: true,
      managerName: adminName || primaryUnit.managerName,
      managerPhone: adminPhone || primaryUnit.managerPhone,
      name: primaryUnitName,
      region: primaryRegion || primaryUnit.region,
      type: primaryUnitType,
    },
    ...draft.units.slice(1).map((unit) => ({
      ...unit,
      isPrimary: false,
    })),
  ]

  return {
    ...draft,
    adminEmail: adminEmail || draft.adminEmail,
    adminName: adminName || draft.adminName,
    adminPhone: adminPhone || draft.adminPhone,
    legalName: legalName || draft.legalName,
    operatingRegions: operatingRegions || draft.operatingRegions,
    organizationName: organizationName || draft.organizationName,
    preferredDriverLanguage: preferredDriverLanguage || draft.preferredDriverLanguage,
    preferredPublicLanguage: preferredPublicLanguage || draft.preferredPublicLanguage,
    primaryCity: primaryCity || draft.primaryCity,
    primaryCountry: primaryCountry || draft.primaryCountry,
    units,
  }
}
